import React, { useEffect } from 'react';

import { TaxDeductionModelEnum } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';

import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { isEmpty, isUndefined } from 'lodash';
import { ContractTermInfoButton } from 'src/components/ContractTermInfoButton';
import { Toggle } from 'src/components/Toggle';
import { ContractTermName } from 'src/constants/contractTermHelpInfo';
import { IContractTermsComponentProps } from 'src/types/IRateCard';
import { hasErrorMessage } from 'src/util/validationsUtil';

import { DEFAULT_EMPTY_TERRITORY, TerritoryBasedGrid, TerritoryBasedGridConfig } from '../TerritoryBasedGrid';
import './StreamingTaxDeduction.scss';

const TAX_DEDUCTION_MODEL_OPTIONS = [
    { value: TaxDeductionModelEnum.GROSS_REVENUE, label: 'Gross Revenue' },
    { value: TaxDeductionModelEnum.PRO_RATA_SHARE, label: 'Pro-Rata Share' }
];

const percentageFormatter = (params: ValueFormatterParams) => {
    if (params.value == null || params.value === '' || params.value == 0) return '0%';
    return new Intl.NumberFormat('en-US', {
        style: 'percent',
        maximumFractionDigits: 6
    }).format(params.value);
};

export const StreamingTaxDeduction = ({
    contractTerms,
    handleContractTermValueChange,
    setIsContractTermInputValid,
    isEditMode,
    handleContractTermErrorMessageChange,
    contractTermErrorMessages
}: IContractTermsComponentProps) => {
    useEffect(() => {
        if (!setIsContractTermInputValid) return;
        setIsContractTermInputValid((prevState) => ({
            ...prevState,
            streamingTaxDeductionCaps: !hasErrorMessage(contractTermErrorMessages!.streamingTaxDeductionCaps)
        }));
    }, [contractTermErrorMessages!.streamingTaxDeductionCaps]);

    // Column definitions for the grid
    const colDefs: ColDef[] = [
        {
            headerName: '',
            field: 'checkBoxBtn',
            floatingFilter: false,
            hide: !isEditMode,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            width: 50,
            pinned: 'left',
            sortable: false
        },
        {
            headerName: 'Territory',
            field: 'territory',
            floatingFilter: false,
            editable: isEditMode,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            sortable: false
        },
        {
            headerName: 'Deduction Model',
            field: 'deductionModel',
            floatingFilter: false,
            editable: isEditMode,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: { values: TAX_DEDUCTION_MODEL_OPTIONS.map((option) => option.value) },
            valueFormatter: (params) => {
                const option = TAX_DEDUCTION_MODEL_OPTIONS.find((opt) => opt.value === params.value);
                return option ? option.label : params.value;
            },
            sortable: false
        },
        {
            headerName: 'Deduction Cap',
            field: 'deductionCap',
            floatingFilter: false,
            editable: isEditMode,
            sortable: false,
            valueFormatter: percentageFormatter
        },
        {
            headerName: 'Tax Rate',
            field: 'taxRate',
            floatingFilter: false,
            editable: isEditMode,
            sortable: false,
            valueFormatter: percentageFormatter
        }
    ];

    // Grid configuration
    const gridConfig: TerritoryBasedGridConfig = {
        contractTermKey: 'streamingTaxDeductionCaps',
        columns: colDefs,
        createNewRow: () => ({
            territory: DEFAULT_EMPTY_TERRITORY,
            deductionModel: TaxDeductionModelEnum.GROSS_REVENUE,
            deductionCap: 0,
            taxRate: 0
        }),
        subheading: 'Territory deduction models and attributes',
        gridClassName: 'streaming-tax-deduction-grid'
    };

    const hasStreamingTaxDeduction = !(
        isUndefined(contractTerms.streamingTaxDeductionCaps) || isEmpty(contractTerms.streamingTaxDeductionCaps)
    );

    return (
        <Box className="box-border-light-gray" type="outline" spacingInset="400">
            <Column>
                <Row spacing="100" alignmentHorizontal="justify">
                    <Row spacing="none">
                        <Heading level={3}>Streaming tax deductions</Heading>
                        <ContractTermInfoButton contractTermName={ContractTermName.StreamingTaxDeduction} />
                    </Row>
                </Row>

                <Column spacing="400">
                    {!isEditMode && (
                        <Row>
                            <Toggle
                                label="Enable deductions for territory specific streaming taxes from royalties calculations"
                                value={hasStreamingTaxDeduction}
                                isEditMode={false}
                            />
                        </Row>
                    )}

                    {(isEditMode || (!isEditMode && hasStreamingTaxDeduction)) && (
                        <TerritoryBasedGrid
                            config={gridConfig}
                            contractTerms={contractTerms}
                            isEditMode={isEditMode}
                            handleContractTermValueChange={handleContractTermValueChange}
                            handleContractTermErrorMessageChange={handleContractTermErrorMessageChange}
                            contractTermErrorMessages={contractTermErrorMessages}
                        />
                    )}
                </Column>
            </Column>
        </Box>
    );
};
